import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Plus, FlaskConical } from "lucide-react";
import { toast } from "sonner";

const ROLES = [["admin", "Administrador"], ["ventas", "Ventas"], ["compras", "Compras"], ["operaciones", "Operaciones"]];
const empty = { email: "", name: "", role: "ventas", password: "", can_sandbox: false };

export default function UsersPage() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/users").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try { await api.post("/users", form); toast.success("Usuario creado"); setOpen(false); setForm(empty); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const toggle = async (u) => { await api.put(`/users/${u.id}`, { active: !u.active }); load(); };
  const toggleSandbox = async (u) => { await api.put(`/users/${u.id}`, { can_sandbox: !u.can_sandbox }); load(); };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="text-3xl font-heading font-bold tracking-tight">Usuarios y Roles</h1><p className="text-muted-foreground text-sm">Segmentación por permisos.</p></div>
        <Button onClick={() => { setForm(empty); setOpen(true); }} data-testid="new-user-btn"><Plus className="w-4 h-4 mr-2" /> Nuevo usuario</Button>
      </div>
      <Card className="rounded-lg overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Nombre</TableHead><TableHead>Email</TableHead><TableHead>Rol</TableHead><TableHead>Activo</TableHead><TableHead>Modo Práctica</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.map((u) => (
              <TableRow key={u.id} data-testid="user-row">
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell><Badge variant="secondary">{u.role}</Badge></TableCell>
                <TableCell><Switch checked={u.active} onCheckedChange={() => toggle(u)} /></TableCell>
                <TableCell><Switch checked={!!u.can_sandbox} onCheckedChange={() => toggleSandbox(u)} data-testid={`user-sandbox-${u.email}`} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nuevo usuario</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Los usuarios con correo @west-kac.com pueden iniciar sesión con Microsoft 365 (SSO); su nombre se toma automáticamente de Microsoft al primer ingreso. La contraseña es opcional (solo para acceso sin Microsoft).</p>
          <div className="space-y-4">
            <div><Label>Nombre</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" /></div>
            <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" /></div>
            <div><Label>Rol</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Contraseña (opcional)</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Dejar vacío para acceso solo con Microsoft" data-testid="user-password" /></div>
            <label className="flex items-center gap-2 cursor-pointer p-3 rounded-md bg-amber-50 border border-amber-200">
              <FlaskConical className="w-4 h-4 text-amber-600" />
              <span className="text-sm flex-1">Puede usar <b>Modo Práctica</b> (entrenamiento en un espacio aislado)</span>
              <Switch checked={!!form.can_sandbox} onCheckedChange={(v) => setForm({ ...form, can_sandbox: v })} data-testid="user-can-sandbox" />
            </label>
          </div>
          <DialogFooter><Button onClick={save} data-testid="save-user">Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
